// =============================================================================
// MANAGEMENT HUB - Identité Entreprise (Source Unique de Vérité)
// =============================================================================
// Configuration centralisée de l'entreprise :
// - Identité légale (raison sociale, SIRET, TVA) + Recherche SIREN
// - Adresse avec autocomplétion BAN
// - Qualifications RGE
// - Assurances (décennale, RC Pro)
// - CGV, mentions légales
// - Paramètres par défaut
// =============================================================================

import React, { useState, useEffect, useCallback } from "react";
import {
  Building2, Shield, Award, FileText, Save, RefreshCw, Plus, Trash2,
  AlertTriangle, CheckCircle2, Info, CreditCard, MapPin, Mail, Phone,
  Globe, FileCheck, Scale, Calendar, ChevronDown, ChevronRight, Settings,
  Search, ExternalLink, Loader2, Image, Upload, X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import SmartLegalEditor from "../../components/editors/SmartLegalEditor";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
// 🔴 N°736 — le régime d'aides DÉCLARÉ gate la section Habilitation ANAH.
import { getRegimeAides, hasIntegrationPennylane } from "../../utils/tenantProfile";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Debounce helper pour les recherches
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Types de qualifications RGE avec leurs logos officiels (Qualit'EnR 2025)
const RGE_QUALIFICATIONS = [
  { 
    code: "QUALIPAC", 
    label: "QualiPAC - Pompes à chaleur",
    logo_url: "https://www.qualit-enr.org/wp-content/uploads/2025/12/Logo-QualiPAC.jpg"
  },
  { 
    code: "QUALIBOIS", 
    label: "QualiBois - Chauffage bois",
    logo_url: "https://www.qualit-enr.org/wp-content/uploads/2025/12/Logo-Qualibois.jpg"
  },
  { 
    code: "QUALISOL", 
    label: "QualiSol - Solaire thermique",
    logo_url: "https://www.qualit-enr.org/wp-content/uploads/2025/12/Logo-Qualisol.jpg"
  },
  { 
    code: "QUALIPV", 
    label: "QualiPV - Photovoltaïque",
    logo_url: "https://www.qualit-enr.org/wp-content/uploads/2025/12/Logo-QualiPV.jpg"
  },
  { 
    code: "QUALIGAZ", 
    label: "QualiGaz - Gaz naturel",
    logo_url: "https://cdn.worldvectorlogo.com/logos/qualigaz.svg"
  },
  { 
    code: "QUALIBAT_RGE", 
    label: "QualiBat RGE - Travaux d'efficacité énergétique",
    logo_url: "https://seeklogo.com/images/R/rge-qualibat-logo-7A10E6F7D4-seeklogo.com.png"
  },
  { 
    code: "ECO_ARTISAN", 
    label: "Éco Artisan - Rénovation énergétique",
    logo_url: "https://www.ecoartisan.net/images/logo-eco-artisan.png"
  },
  { 
    code: "PROS_PERF", 
    label: "Pros de la performance énergétique",
    logo_url: "https://www.presdechezvous.fr/images/labels/feebat.png"
  },
];

// Formes juridiques
const FORMES_JURIDIQUES = [
  { code: "SARL", label: "SARL" },
  { code: "SAS", label: "SAS" },
  { code: "SASU", label: "SASU" },
  { code: "EURL", label: "EURL" },
  { code: "EI", label: "Entreprise Individuelle" },
  { code: "SA", label: "SA" },
  { code: "SCI", label: "SCI" },
  { code: "AUTO", label: "Auto-entrepreneur" },
];

const getAuthHeaders = () => {
  // Le reste de l'app stocke sous "session_token" (cf. AuthContext) —
  // l'ancien lecteur "token" renvoyait toujours {} et le Bearer n'arrivait
  // jamais au backend (le cookie sauvait la mise sur GET/PUT mais pas
  // toujours sur les uploads PWA / SW).
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Compresse une image > maxSizeMB en JPEG via canvas. SVG ou déjà petite → renvoyée telle quelle.
// Réduit dimensions puis qualité jusqu'à passer sous la limite.
const compressImage = (file, maxSizeMB = 2) => new Promise((resolve, reject) => {
  if (!file) return reject(new Error("Aucun fichier"));
  if (file.type === "image/svg+xml" || /\.svg$/i.test(file.name || "")) return resolve(file);
  if (file.size <= maxSizeMB * 1024 * 1024) return resolve(file);

  const reader = new FileReader();
  reader.onerror = () => reject(new Error("Lecture image échouée"));
  reader.onload = (ev) => {
    const img = new Image();
    img.onerror = () => reject(new Error("Décodage image échoué"));
    img.onload = async () => {
      const maxBytes = maxSizeMB * 1024 * 1024;
      let w = img.width, h = img.height;
      let quality = 0.92;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
      const encode = () => new Promise((res) => {
        canvas.width = w; canvas.height = h;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((b) => res(b), "image/jpeg", quality);
      });
      try {
        for (let i = 0; i < 8; i++) {
          const blob = await encode();
          if (!blob) return reject(new Error("Encodage canvas échoué"));
          if (blob.size <= maxBytes) {
            return resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
          }
          if (Math.max(w, h) > 800) { w = Math.round(w * 0.8); h = Math.round(h * 0.8); }
          else if (quality > 0.5) { quality -= 0.1; }
          else { return resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" })); }
        }
      } catch (err) {
        reject(err);
      }
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
});

// Upload via XHR — plus robuste que fetch sur Android Chrome PWA pour multipart
// (fetch retourne parfois "TypeError: Failed to fetch" quand l'onglet est suspendu
// pendant la sélection du fichier).
const xhrUpload = (url, formData, headers = {}) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open("POST", url, true);
  Object.entries(headers).forEach(([k, v]) => {
    if (v != null && v !== "") xhr.setRequestHeader(k, v);
  });
  xhr.timeout = 60000;
  xhr.onload = () => {
    let data = null;
    try { data = JSON.parse(xhr.responseText || "null"); } catch { data = xhr.responseText; }
    resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
  };
  xhr.onerror = () => reject(new Error(`Network error (XHR status=${xhr.status || 0})`));
  xhr.ontimeout = () => reject(new Error("Timeout upload (60s)"));
  xhr.send(formData);
});

// Section collapsible avec indicateur de complétion
const Section = ({ title, icon: Icon, iconColor, isComplete, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColor}`}>
              <Icon className="w-5 h-5" />
            </div>
            <span className="font-medium text-white">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            {isComplete ? (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Complet
              </Badge>
            ) : (
              <Badge className="bg-amber-500/20 text-amber-400 border-0">
                <AlertTriangle className="w-3 h-3 mr-1" /> À compléter
              </Badge>
            )}
            {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 border-l-2 border-slate-700 ml-5 mt-2 space-y-4">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Composant dédié pour saisie des départements (conversion array ↔ string au blur)
const DepartementsInput = ({ value, onChange }) => {
  const [text, setText] = useState((value || []).join(", "));
  
  // Sync si la valeur extérieure change (ex: suppression d'un badge)
  useEffect(() => {
    setText((value || []).join(", "));
  }, [value]);

  const commitValue = () => {
    const deps = text.split(",").map(d => d.trim()).filter(Boolean);
    onChange(deps);
  };

  return (
    <Input
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commitValue}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitValue(); } }}
      placeholder="Ex: 81, 31, 82, 11"
      className="bg-slate-900/50 border-slate-600"
      data-testid="departements-input"
    />
  );
};


export default function IdentiteEntreprise({ tenantSlug = "jkc" }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  
  // États pour la recherche SIREN
  const [entrepriseQuery, setEntrepriseQuery] = useState("");
  const [entrepriseSuggestions, setEntrepriseSuggestions] = useState([]);
  const [loadingEntreprise, setLoadingEntreprise] = useState(false);
  const [showEntrepriseSuggestions, setShowEntrepriseSuggestions] = useState(false);
  const debouncedEntreprise = useDebounce(entrepriseQuery, 400);
  
  // États pour la recherche adresse
  const [addressQuery, setAddressQuery] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const debouncedAddress = useDebounce(addressQuery, 300);
  
  const [identite, setIdentite] = useState({
    // Identité légale
    raison_sociale: "",
    forme_juridique: "",
    siret: "",
    code_naf: "",
    tva_intracommunautaire: "",
    rcs: "",
    capital_social: "",
    // Adresse
    adresse: "",
    complement_adresse: "",
    code_postal: "",
    ville: "",
    pays: "France",
    // Contact
    telephone: "",
    email: "",
    email_envoi: "",
    site_web: "",
    // Logo entreprise
    logo_url: "",
    // Bancaire
    iban: "",
    bic: "",
    banque: "",
    // RGE
    rge_qualifications: [],
    // Assurances
    assurance_decennale: { compagnie: "", numero_police: "", date_validite: "" },
    assurance_rc_pro: { compagnie: "", numero_police: "", date_validite: "" },
    // Légal
    cgv: "",
    mentions_legales: "",
    politique_rgpd: "",
    // Paramètres
    taux_tva_defaut: 20.0,
    delai_paiement_defaut: 30,
    // Pennylane
    email_pennylane_achats: "",
    email_pennylane_ventes: "",
    // Habilitation ANAH
    habilitation_anah: "",
    departements_couverts: [],
    // Tampon + Signature
    signature_url: "",
    tampon_url: "",
    // Représentant légal (apposé sur Contrat AMO, Devis AMO, Mandat ANAH)
    representant: "",
    representant_fonction: "",
    // 🔴 #736 — PRÉSIDENCE EXERCÉE PAR UNE PERSONNE MORALE.
    // ⚠️ Champ STRUCTURÉ déjà déclaré au modèle et déjà lu par le Contrat AMO,
    // le Devis AMO et le Mandat ANAH. ⛔ Aucun champ plat en double : deux
    // sources pour un même fait juridique divergeraient, et la divergence ne se
    // verrait que sur des actes signés.
    dirigeant_personne_morale: null,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // N°1003-B2 — « Inclure les CGV dans les devis » : réglage du gabarit devis utilisé par
  // le PDF (serveur = autorité). Le contenu des CGV ci-dessous n'est jamais effacé.
  const [cgvIncluses, setCgvIncluses] = useState(null);
  const [cgvErreur, setCgvErreur] = useState("");

  useEffect(() => {
    let vivant = true;
    fetch(`${API}/api/metier/devis-template/cgv-incluses`, {
      headers: { ...getAuthHeaders(), ...enteteTenant(tenantSlug) }, credentials: "include",
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!vivant) return;
        if (r.ok) { setCgvIncluses(!!d.incluses); setCgvErreur(""); }
        else setCgvErreur(d.detail || `Réglage CGV indisponible (${r.status})`);
      })
      .catch((e) => vivant && setCgvErreur(e.message));
    return () => { vivant = false; };
  }, [tenantSlug]);

  const changerCgvIncluses = async (valeur) => {
    setCgvErreur("");
    try {
      const r = await fetch(`${API}/api/metier/devis-template/cgv-incluses`, {
        method: "PUT", credentials: "include",
        headers: { ...getAuthHeaders(), ...enteteTenant(tenantSlug), "Content-Type": "application/json" },
        body: JSON.stringify({ incluses: valeur }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Échec (${r.status})`);
      setCgvIncluses(!!d.incluses);
      toast.success(d.incluses ? "CGV incluses dans les devis" : "CGV non incluses dans les devis");
    } catch (e) {
      setCgvErreur(e.message);
    }
  };
  // Diag upload tampon/signature : visible inline (mobile sans devtools)
  const [uploadDebug, setUploadDebug] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/platform/tenants/${tenantSlug}/identite`, {
        headers: { ...getAuthHeaders(), ...enteteTenant(tenantSlug) }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTenantInfo({ nom: data.tenant_nom, slug: data.tenant_slug });
        
        if (data.identite) {
          // Extraire le contenu HTML des CGV si c'est un objet structuré
          let cgvContent = "";
          const cgvData = data.identite.cgv;
          if (cgvData) {
            if (typeof cgvData === "string") {
              cgvContent = cgvData;
            } else if (typeof cgvData === "object") {
              cgvContent = cgvData.contenu_html || "";
            }
          }
          
          setIdentite(prev => ({
            ...prev,
            ...data.identite,
            cgv: cgvContent, // Stocker juste le contenu pour l'édition
            rge_qualifications: data.identite.rge_qualifications || [],
            assurance_decennale: { ...prev.assurance_decennale, ...data.identite.assurance_decennale },
            assurance_rc_pro: { ...prev.assurance_rc_pro, ...data.identite.assurance_rc_pro },
          }));
        }
      }
    } catch (err) {
      console.error("Erreur chargement identité:", err);
    } finally {
      setLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Recherche entreprise via API officielle (recherche-entreprises.api.gouv.fr)
  useEffect(() => {
    const searchEntreprise = async () => {
      if (!debouncedEntreprise || debouncedEntreprise.length < 3) {
        setEntrepriseSuggestions([]);
        return;
      }
      
      setLoadingEntreprise(true);
      try {
        const cleanQuery = debouncedEntreprise.replace(/\s/g, '');
        const isSiren = /^\d{9,14}$/.test(cleanQuery);
        
        const url = `https://recherche-entreprises.api.gouv.fr/search?q=${
          isSiren ? cleanQuery : encodeURIComponent(debouncedEntreprise)
        }&per_page=5`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.results && data.results.length > 0) {
          setEntrepriseSuggestions(data.results.map(r => ({
            siren: r.siren,
            siret: r.siege?.siret,
            nom: r.nom_complet || r.nom_raison_sociale,
            forme_juridique: r.nature_juridique || "",
            adresse: r.siege?.adresse || "",
            code_postal: r.siege?.code_postal || "",
            ville: r.siege?.libelle_commune || "",
            activité: r.siege?.activite_principale || ""
          })));
          setShowEntrepriseSuggestions(true);
        } else {
          setEntrepriseSuggestions([]);
        }
      } catch (err) {
        console.error('Erreur API Entreprise:', err);
        setEntrepriseSuggestions([]);
      } finally {
        setLoadingEntreprise(false);
      }
    };
    
    searchEntreprise();
  }, [debouncedEntreprise]);

  // Autocomplétion adresse via API BAN (data.gouv.fr)
  useEffect(() => {
    const searchAddress = async () => {
      if (!debouncedAddress || debouncedAddress.length < 3) {
        setAddressSuggestions([]);
        return;
      }
      
      setLoadingAddress(true);
      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(debouncedAddress)}&limit=5`
        );
        const data = await response.json();
        
        if (data.features) {
          setAddressSuggestions(data.features.map(f => ({
            label: f.properties.label,
            street: f.properties.name,
            postcode: f.properties.postcode,
            city: f.properties.city,
            context: f.properties.context
          })));
          setShowAddressSuggestions(true);
        }
      } catch (err) {
        console.error('Erreur API Adresse:', err);
      } finally {
        setLoadingAddress(false);
      }
    };
    
    searchAddress();
  }, [debouncedAddress]);

  // Handler sélection entreprise depuis API SIREN
  const handleSelectEntreprise = (suggestion) => {
    // Map code forme juridique vers label
    const mapFormeJuridique = (code) => {
      const mapping = {
        '5710': 'SAS', '5720': 'SASU', '5498': 'EURL',
        '5499': 'SARL', '5599': 'SA', '1000': 'EI'
      };
      return mapping[code] || code || '';
    };
    
    setIdentite(prev => ({
      ...prev,
      raison_sociale: suggestion.nom,
      siret: suggestion.siret || "",
      forme_juridique: mapFormeJuridique(suggestion.forme_juridique),
      adresse: suggestion.adresse,
      code_postal: suggestion.code_postal,
      ville: suggestion.ville
    }));
    setEntrepriseQuery("");
    setShowEntrepriseSuggestions(false);
    setEntrepriseSuggestions([]);
    setHasChanges(true);
  };

  // Handler sélection adresse depuis API BAN
  const handleSelectAddress = (suggestion) => {
    setIdentite(prev => ({
      ...prev,
      adresse: suggestion.street,
      code_postal: suggestion.postcode,
      ville: suggestion.city
    }));
    setAddressQuery("");
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      // Préparer les données avec CGV au format structuré
      const identiteToSave = {
        ...identite,
        // Convertir CGV string en CGVConfig structuré
        cgv: identite.cgv ? {
          version_courante: "2025.1",
          contenu_html: identite.cgv,
          date_effet: new Date().toISOString()
        } : null
      };
      
      const res = await fetch(`${API}/api/platform/tenants/${tenantSlug}/identite`, {
        method: "PUT",
        headers: { 
          ...getAuthHeaders(),
          ...enteteTenant(tenantSlug),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ identite: identiteToSave }),
      });
      
      if (res.ok) {
        setSaveSuccess(true);
        setHasChanges(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        const error = await res.json();
        alert(error.detail || "Erreur lors de la sauvegarde");
      }
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      alert("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setIdentite(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const updateNestedField = (parent, field, value) => {
    setIdentite(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
    setHasChanges(true);
  };

  const addRgeQualification = () => {
    setIdentite(prev => ({
      ...prev,
      rge_qualifications: [...prev.rge_qualifications, { code: "", numero: "", date_validite: "" }]
    }));
    setHasChanges(true);
  };

  const updateRgeQualification = (index, field, value) => {
    setIdentite(prev => {
      const newQuals = [...prev.rge_qualifications];
      newQuals[index] = { ...newQuals[index], [field]: value };
      
      // Auto-remplir le logo quand on sélectionne une qualification
      if (field === "code") {
        const qualificationInfo = RGE_QUALIFICATIONS.find(q => q.code === value);
        if (qualificationInfo?.logo_url && !newQuals[index].logo_url) {
          newQuals[index].logo_url = qualificationInfo.logo_url;
        }
      }
      
      return { ...prev, rge_qualifications: newQuals };
    });
    setHasChanges(true);
  };

  const removeRgeQualification = (index) => {
    setIdentite(prev => ({
      ...prev,
      rge_qualifications: prev.rge_qualifications.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  // Vérification de complétion des sections
  const isIdentiteLegaleComplete = identite.raison_sociale && identite.siret;
  const isAdresseComplete = identite.adresse && identite.code_postal && identite.ville;
  const isRgeComplete = identite.rge_qualifications.length > 0 && identite.rge_qualifications.every(q => q.code && q.date_validite);
  const isAssuranceComplete = identite.assurance_decennale?.compagnie && identite.assurance_decennale?.date_validite;

  // Alertes
  const getAlerts = () => {
    const alerts = [];
    
    // RGE expiré ou proche expiration
    identite.rge_qualifications.forEach(qual => {
      if (qual.date_validite) {
        const expDate = new Date(qual.date_validite);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
        
        if (daysUntilExpiry < 0) {
          alerts.push({ type: "error", message: `RGE ${qual.code} expiré depuis ${-daysUntilExpiry} jours` });
        } else if (daysUntilExpiry < 30) {
          alerts.push({ type: "warning", message: `RGE ${qual.code} expire dans ${daysUntilExpiry} jours` });
        }
      }
    });
    
    // Assurance décennale
    if (identite.assurance_decennale?.date_validite) {
      const expDate = new Date(identite.assurance_decennale.date_validite);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) {
        alerts.push({ type: "error", message: `Assurance décennale expirée depuis ${-daysUntilExpiry} jours` });
      } else if (daysUntilExpiry < 30) {
        alerts.push({ type: "warning", message: `Assurance décennale expire dans ${daysUntilExpiry} jours` });
      }
    }
    
    return alerts;
  };

  const alerts = getAlerts();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-emerald-400" />
            Identité Entreprise
          </h1>
          <p className="text-slate-400 text-sm">
            {tenantInfo?.nom} • Source unique de vérité pour les informations légales
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saveSuccess && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Enregistré
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-identite-btn"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Alertes */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, i) => (
            <div 
              key={i}
              className={`p-3 rounded-lg flex items-center gap-2 ${
                alert.type === "error" 
                  ? "bg-red-500/10 border border-red-500/30 text-red-400" 
                  : "bg-amber-500/10 border border-amber-500/30 text-amber-400"
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">{alert.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-blue-300 font-medium">Ces informations sont utilisées sur tous vos documents</p>
            <p className="text-blue-400/80 text-sm mt-1">
              Devis, factures, attestations... Toute modification ici sera reflétée automatiquement.
            </p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {/* Identité Légale */}
        <Section 
          title="Identité légale" 
          icon={Building2} 
          iconColor="bg-emerald-500/20 text-emerald-400"
          isComplete={isIdentiteLegaleComplete}
          defaultOpen={true}
        >
          {/* Recherche entreprise par nom ou SIREN */}
          <div className="relative mb-4 pb-4 border-b border-slate-700/50">
            <label className="text-xs text-emerald-400 mb-1 block flex items-center gap-1">
              <Search className="w-3 h-3" />
              Rechercher une entreprise (nom ou SIREN)
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input 
                value={entrepriseQuery}
                onChange={(e) => setEntrepriseQuery(e.target.value)}
                onFocus={() => entrepriseSuggestions.length > 0 && setShowEntrepriseSuggestions(true)}
                onBlur={() => setTimeout(() => setShowEntrepriseSuggestions(false), 200)}
                className="w-full pl-10 pr-10 py-2 bg-emerald-900/20 border border-emerald-500/30 rounded-lg text-white placeholder:text-zinc-400 focus:outline-none focus:border-emerald-500/50"
                placeholder="Tapez un nom d'entreprise ou un SIREN..."
                data-testid="search-siren"
              />
              {loadingEntreprise && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-emerald-400" />
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              Recherche via recherche-entreprises.api.gouv.fr
            </p>
            
            {/* Suggestions dropdown */}
            {showEntrepriseSuggestions && entrepriseSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-72 overflow-y-auto">
                {entrepriseSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectEntreprise(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-700 transition-colors border-b border-slate-700/50 last:border-0"
                    data-testid={`siren-suggestion-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{suggestion.nom}</p>
                        <p className="text-xs text-slate-400 truncate">
                          {suggestion.adresse && `${suggestion.adresse}, `}{suggestion.code_postal} {suggestion.ville}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-mono text-emerald-400">{suggestion.siren}</p>
                        {suggestion.forme_juridique && (
                          <p className="text-xs text-zinc-400">{suggestion.forme_juridique}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Raison sociale *</label>
              <Input
                value={identite.raison_sociale}
                onChange={(e) => updateField("raison_sociale", e.target.value)}
                placeholder="Ex: Climat PAC 81 SARL"
                className="bg-slate-900/50 border-slate-600"
                data-testid="raison-sociale"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Forme juridique</label>
              <Select value={identite.forme_juridique} onValueChange={(v) => updateField("forme_juridique", v)}>
                <SelectTrigger className="bg-slate-900/50 border-slate-600">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {FORMES_JURIDIQUES.map(f => (
                    <SelectItem key={f.code} value={f.code}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">SIRET *</label>
              <Input
                value={identite.siret}
                onChange={(e) => updateField("siret", e.target.value)}
                placeholder="123 456 789 00012"
                className="bg-slate-900/50 border-slate-600"
                data-testid="siret"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Code NAF</label>
              <Input
                value={identite.code_naf}
                onChange={(e) => updateField("code_naf", e.target.value)}
                placeholder="4322A"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">N° TVA Intracommunautaire</label>
              <Input
                value={identite.tva_intracommunautaire}
                onChange={(e) => updateField("tva_intracommunautaire", e.target.value)}
                placeholder="FR12345678901"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">RCS</label>
              <Input
                value={identite.rcs || ""}
                onChange={(e) => updateField("rcs", e.target.value)}
                placeholder="RCS Paris 123 456 789"
                className="bg-slate-900/50 border-slate-600"
                data-testid="rcs"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Capital social</label>
              <Input
                value={identite.capital_social}
                onChange={(e) => updateField("capital_social", e.target.value)}
                placeholder="10 000 €"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            {/* 🔴 #736 — PRÉSIDENCE. La case n'est PAS un champ stocké : elle EST
                la présence de `dirigeant_personne_morale`. ⛔ Un booléen à côté
                serait une seconde source, et les deux se contrediraient un jour.
                ⚠️ Décocher REMET le champ à null : sans cela, la structure
                masquée continuerait de primer dans le Contrat AMO, en silence. */}
            <div className="md:col-span-2 border-t border-slate-700 pt-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!identite.dirigeant_personne_morale}
                  onChange={(e) => updateField(
                    "dirigeant_personne_morale",
                    e.target.checked
                      ? { denomination: "", forme_juridique: "", siren: "",
                          qualite: "", representant: "", representant_qualite: "" }
                      : null)}
                  data-testid="presidence-personne-morale"
                  className="accent-blue-500"
                />
                <span className="text-sm text-slate-200">
                  Le président est une personne morale
                </span>
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Une présidence exercée par une société porte deux niveaux : la
                société dirigeante, et la personne physique qui la représente.
              </p>
            </div>

            {!identite.dirigeant_personne_morale ? (
              <>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Représentant légal</label>
                  <Input
                    value={identite.representant || ""}
                    onChange={(e) => updateField("representant", e.target.value)}
                    placeholder="Ex: Jean DUPONT"
                    className="bg-slate-900/50 border-slate-600"
                    data-testid="representant"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Fonction du représentant</label>
                  <Input
                    value={identite.representant_fonction || ""}
                    onChange={(e) => updateField("representant_fonction", e.target.value)}
                    placeholder="Ex: Président, Gérant"
                    className="bg-slate-900/50 border-slate-600"
                    data-testid="representant-fonction"
                  />
                </div>
              </>
            ) : (
              <>
                {[
                  ["denomination", "Dénomination de la société dirigeante", "Ex: SOCIÉTÉ EXEMPLE"],
                  ["forme_juridique", "Forme juridique", "Ex: SAS"],
                  ["siren", "SIREN", "Ex: 000000000"],
                  ["qualite", "Qualité de la société dans l'entreprise", "Ex: Président"],
                  ["representant", "Représentée par (personne physique)", "Ex: NOM EXEMPLE"],
                  ["representant_qualite", "Qualité de cette personne DANS la société dirigeante", "Ex: son Président"],
                ].map(([cle, libelle, exemple]) => (
                  <div key={cle}>
                    <label className="text-xs text-slate-400 mb-1 block">{libelle}</label>
                    <Input
                      value={identite.dirigeant_personne_morale?.[cle] || ""}
                      onChange={(e) => updateNestedField(
                        "dirigeant_personne_morale", cle, e.target.value)}
                      placeholder={exemple}
                      className="bg-slate-900/50 border-slate-600"
                      data-testid={`dirigeant-${cle}`}
                    />
                  </div>
                ))}
                {/* ⚠️ « Qualité » et « qualité du représentant » ne sont PAS
                    redondants : l'une est le rôle de la SOCIÉTÉ vis-à-vis de
                    l'entreprise, l'autre celui de la PERSONNE dans cette
                    société. Les confondre reproduit l'erreur que ce modèle
                    corrige. */}
                <p className="md:col-span-2 text-xs text-slate-500">
                  Le champ « Représentant légal » plat n'est plus publié tant que
                  ce bloc est renseigné — il reste conservé, non affiché.
                </p>
              </>
            )}
          </div>
        </Section>

        {/* Adresse */}
        <Section 
          title="Adresse" 
          icon={MapPin} 
          iconColor="bg-blue-500/20 text-blue-400"
          isComplete={isAdresseComplete}
        >
          {/* Recherche adresse avec autocomplétion */}
          <div className="relative mb-4 pb-4 border-b border-slate-700/50">
            <label className="text-xs text-blue-400 mb-1 block flex items-center gap-1">
              <Search className="w-3 h-3" />
              Rechercher une adresse
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input 
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                onFocus={() => addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 200)}
                className="w-full pl-10 pr-10 py-2 bg-blue-900/20 border border-blue-500/30 rounded-lg text-white placeholder:text-zinc-400 focus:outline-none focus:border-blue-500/50"
                placeholder="Tapez une adresse pour rechercher..."
                data-testid="search-address"
              />
              {loadingAddress && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-400" />
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              Autocomplétion via api-adresse.data.gouv.fr (BAN)
            </p>
            
            {/* Suggestions dropdown */}
            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {addressSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectAddress(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-700 transition-colors border-b border-slate-700/50 last:border-0"
                    data-testid={`address-suggestion-${idx}`}
                  >
                    <p className="text-white text-sm">{suggestion.label}</p>
                    <p className="text-xs text-zinc-400">{suggestion.context}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-400 mb-1 block">Adresse *</label>
              <Input
                value={identite.adresse}
                onChange={(e) => updateField("adresse", e.target.value)}
                placeholder="123 rue de l'exemple"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-slate-400 mb-1 block">Complément d'adresse</label>
              <Input
                value={identite.complement_adresse}
                onChange={(e) => updateField("complement_adresse", e.target.value)}
                placeholder="Bâtiment B, étage 2"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Code postal *</label>
              <Input
                value={identite.code_postal}
                onChange={(e) => updateField("code_postal", e.target.value)}
                placeholder="81000"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ville *</label>
              <Input
                value={identite.ville}
                onChange={(e) => updateField("ville", e.target.value)}
                placeholder="Albi"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
          </div>
        </Section>

        {/* Contact */}
        <Section 
          title="Contact" 
          icon={Phone} 
          iconColor="bg-purple-500/20 text-purple-400"
          /* 🔴 ET, ⛔ PAS OU. N°490.
             ⚠️ MESURÉ : avec un OU, le téléphone seul suffisait à afficher
             « Complet » pendant que l'email restait VIDE — et le PDF, lui,
             rend LES DEUX. Un indicateur qui dit « complet » sur une identité
             que les documents émis jugent incomplète est un indicateur qui
             MENT : c'est la même famille que le champ « requis » qui
             n'empêchait rien.
             🔑 « Complet » doit signifier COMPLET AU SENS DES DOCUMENTS. */
          isComplete={!!(identite.telephone && identite.email)}
          /* 🔴 ET ELLE S'OUVRE TANT QU'ELLE EST INCOMPLÈTE.
             ⚠️ Repliée par défaut, la liste visible s'arrêtait à « Capital
             social » — d'où la conclusion, raisonnable et fausse, que les
             champs téléphone et email N'EXISTAIENT PAS. Ils existaient :
             ils étaient CACHÉS. Une section qu'il reste à remplir ⛔ ne doit
             pas se dissimuler. */
          defaultOpen={!(identite.telephone && identite.email)}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Téléphone</label>
              <Input
                value={identite.telephone}
                onChange={(e) => updateField("telephone", e.target.value)}
                placeholder="05 63 XX XX XX"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Email contact</label>
              <Input
                value={identite.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="contact@exemple.invalid"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                Email expéditeur (envois automatiques)
              </label>
              <Input
                value={identite.email_envoi || ""}
                onChange={(e) => updateField("email_envoi", e.target.value)}
                placeholder="Optionnel — sinon utilise Email contact"
                className="bg-slate-900/50 border-slate-600"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Adresse utilisée comme expéditeur pour tous les envois OmniHub
                (notifications client, rapports, confirmations RDV). Si vide,
                l'email de contact est utilisé.
              </p>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Site web</label>
              <Input
                value={identite.site_web}
                onChange={(e) => updateField("site_web", e.target.value)}
                placeholder="https://www.exemple.invalid"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
          </div>
        </Section>

        {/* Logo Entreprise */}
        <Section 
          title="Logo Entreprise" 
          icon={Image} 
          iconColor="bg-pink-500/20 text-pink-400"
          isComplete={!!identite.logo_url}
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-400">
              Le logo apparaîtra sur vos devis, factures et documents contractuels.
            </p>
            
            {/* Aperçu du logo */}
            {identite.logo_url ? (
              <div className="flex items-center gap-4">
                <div className="w-32 h-32 bg-slate-800 rounded-lg border border-slate-600 flex items-center justify-center overflow-hidden">
                  <img 
                    src={identite.logo_url} 
                    alt="Logo entreprise" 
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden flex-col items-center text-zinc-400">
                    <AlertTriangle className="w-6 h-6 mb-1" />
                    <span className="text-xs">Image invalide</span>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-slate-400 mb-1 block">URL du logo</label>
                  <Input
                    value={identite.logo_url}
                    onChange={(e) => updateField("logo_url", e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="bg-slate-900/50 border-slate-600"
                  />
                  <p className="text-xs text-zinc-400 mt-1">
                    Format recommandé : PNG ou SVG, fond transparent, min 200x200px
                  </p>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center">
                <Upload className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
                <label className="text-xs text-slate-400 mb-2 block">URL du logo</label>
                <Input
                  value={identite.logo_url}
                  onChange={(e) => updateField("logo_url", e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="bg-slate-900/50 border-slate-600 max-w-md mx-auto"
                />
                <p className="text-xs text-zinc-400 mt-2">
                  Collez l'URL de votre logo (hébergé sur votre serveur ou un service cloud)
                </p>
              </div>
            )}
          </div>
        </Section>

        {/* Qualifications RGE */}
        <Section 
          title="Qualifications RGE" 
          icon={Award} 
          iconColor="bg-emerald-500/20 text-emerald-400"
          isComplete={isRgeComplete}
        >
          <div className="space-y-4">
            {identite.rge_qualifications.length === 0 ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
                <AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-amber-300 text-sm">Aucune qualification configurée</p>
                <p className="text-amber-400/70 text-xs mt-1">
                  Les qualifications sont nécessaires pour permettre à vos clients de bénéficier des aides.
                </p>
              </div>
            ) : (
              identite.rge_qualifications.map((qual, index) => (
                <div key={index} className="bg-slate-900/30 border border-slate-700/50 rounded-lg p-4">
                  <div className="flex items-start gap-4">
                    {/* Logo de la qualification */}
                    <div className="flex-shrink-0">
                      {qual.logo_url ? (
                        <div className="w-16 h-16 bg-zinc-900/50 rounded-lg border border-slate-600 flex items-center justify-center overflow-hidden">
                          <img 
                            src={qual.logo_url} 
                            alt={`Logo ${qual.code}`}
                            className="max-w-full max-h-full object-contain p-1"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-16 h-16 bg-slate-800 rounded-lg border border-dashed border-slate-600 flex items-center justify-center">
                          <Award className="w-6 h-6 text-zinc-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">Qualification *</label>
                          <Select value={qual.code} onValueChange={(v) => updateRgeQualification(index, "code", v)}>
                            <SelectTrigger className="bg-slate-900/50 border-slate-600">
                              <SelectValue placeholder="Sélectionner" />
                            </SelectTrigger>
                            <SelectContent>
                              {RGE_QUALIFICATIONS.map(q => (
                                <SelectItem key={q.code} value={q.code}>{q.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">N° Certificat</label>
                          <Input
                            value={qual.numero}
                            onChange={(e) => updateRgeQualification(index, "numero", e.target.value)}
                            placeholder="RGE-XXXX-XXXX"
                            className="bg-slate-900/50 border-slate-600"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">Date de validité *</label>
                          <Input
                            type="date"
                            value={qual.date_validite}
                            onChange={(e) => updateRgeQualification(index, "date_validite", e.target.value)}
                            className="bg-slate-900/50 border-slate-600"
                          />
                        </div>
                      </div>
                      {/* URL du logo RGE avec bouton auto */}
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="text-xs text-slate-400 mb-1 block">Logo qualification</label>
                          <Input
                            value={qual.logo_url || ""}
                            onChange={(e) => updateRgeQualification(index, "logo_url", e.target.value)}
                            placeholder="URL auto-remplie si disponible"
                            className="bg-slate-900/50 border-slate-600"
                          />
                        </div>
                        {qual.code && RGE_QUALIFICATIONS.find(q => q.code === qual.code)?.logo_url && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const qualInfo = RGE_QUALIFICATIONS.find(q => q.code === qual.code);
                              if (qualInfo?.logo_url) {
                                updateRgeQualification(index, "logo_url", qualInfo.logo_url);
                              }
                            }}
                            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-9"
                          >
                            Logo officiel
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRgeQualification(index)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
            <Button
              variant="outline"
              onClick={addRgeQualification}
              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
              data-testid="add-rge-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une qualification
            </Button>
          </div>
        </Section>

        {/* 🔴🔴 N°736 — « Habilitation ANAH & Départements », l'écran CIBLE du
            bouton « Modifier dans Entreprise ». ⚠️ MESURÉ sur mario_bat : la
            section était rendue SANS AUCUNE condition, et son encart dit
            explicitement « pour l'activité MaPrimeAdapt' ».
            🔑 Même autorité que l'écran Configuration : le régime d'aides
            DÉCLARÉ (tef/omv/climat en ont un ⇒ ⛔ inchangés). */}
        {getRegimeAides().length > 0 && (
        <Section 
          title="Habilitation ANAH & Départements" 
          icon={Award} 
          iconColor="bg-teal-500/20 text-teal-400"
          isComplete={!!identite.habilitation_anah}
        >
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                <p className="text-xs text-blue-300">
                  Renseignez votre numéro d'habilitation ANAH et les départements couverts pour l'activité MaPrimeAdapt'.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">N° Habilitation ANAH *</label>
                <Input
                  value={identite.habilitation_anah || ""}
                  onChange={(e) => updateField("habilitation_anah", e.target.value)}
                  placeholder="Ex: HAB-2024-XXXX"
                  className="bg-slate-900/50 border-slate-600"
                  data-testid="habilitation-anah-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Départements couverts</label>
                <DepartementsInput
                  value={identite.departements_couverts || []}
                  onChange={(deps) => updateField("departements_couverts", deps)}
                />
                <p className="text-xs text-zinc-400 mt-1">Tapez les numéros séparés par des virgules, puis cliquez ailleurs pour valider</p>
              </div>
            </div>

            {/* Affichage des départements sélectionnés */}
            {identite.departements_couverts?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {identite.departements_couverts.map((dep, idx) => (
                  <Badge key={idx} className="bg-teal-500/20 text-teal-400 border-teal-500/30 cursor-pointer" onClick={() => {
                    const newDeps = identite.departements_couverts.filter((_, i) => i !== idx);
                    updateField("departements_couverts", newDeps);
                  }}>
                    {dep} <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </Section>
        )}

        {/* Tampon + Signature entreprise */}
        <Section 
          title="Tampon + Signature" 
          icon={Image} 
          iconColor="bg-rose-500/20 text-rose-400"
          isComplete={!!(identite.signature_url || identite.tampon_url)}
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-400">
              Uploadez votre tampon et/ou signature. Ils seront apposés automatiquement sur les Devis AMO, Contrats AMO et Mandats ANAH générés.
            </p>
            {uploadDebug && (
              <div
                className="text-[11px] font-mono whitespace-pre-wrap break-all rounded-md border p-2"
                style={{
                  background: "var(--oh-bg-muted, rgba(15,23,42,.6))",
                  borderColor: "var(--oh-border, #334155)",
                  color: "var(--oh-text, #e2e8f0)",
                }}
                data-testid="upload-debug-banner"
              >
                {uploadDebug}
                <button
                  type="button"
                  onClick={() => setUploadDebug("")}
                  className="ml-2 underline opacity-70"
                  aria-label="Effacer le diagnostic upload"
                >
                  effacer
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Signature */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-rose-400" />
                  Signature
                </h4>
                {identite.signature_url ? (
                  <div className="relative group">
                    <div className="rounded-lg border border-slate-700 p-3 bg-slate-900/50 flex items-center gap-3">
                      <img 
                        src={`${API}${identite.signature_url}`} 
                        alt="Signature" 
                        className="h-16 object-contain bg-white/5 rounded p-1" 
                        data-testid="signature-preview"
                      />
                      <div className="flex-1">
                        <p className="text-xs text-emerald-400 font-medium">Signature uploadée</p>
                        <p className="text-xs text-slate-500 truncate">{identite.signature_url.split('/').pop()}</p>
                      </div>
                      <Button 
                        variant="ghost" size="sm" 
                        onClick={async () => {
                          if (!window.confirm("Supprimer la signature ?")) return;
                          try {
                            const res = await fetch(`${API}/api/platform/tenants/${tenantSlug}/delete-signature?type=signature`, {
                              method: "DELETE",
                              headers: { ...getAuthHeaders(), ...enteteTenant(tenantSlug) }
                            });
                            if (res.ok) {
                              updateField("signature_url", "");
                              toast.success("Signature supprimée");
                            } else {
                              const txt = await res.text().catch(() => "");
                              console.error("[delete signature] HTTP", res.status, txt);
                              toast.error(`Échec suppression signature (HTTP ${res.status})`);
                            }
                          } catch (err) {
                            console.error("Delete signature error:", err);
                            toast.error(`Erreur suppression : ${err?.message || err}`);
                          }
                        }}
                        className="text-red-400 hover:text-red-300"
                        data-testid="delete-signature-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-28 rounded-lg border-2 border-dashed border-slate-700 hover:border-rose-500/50 cursor-pointer transition-colors bg-slate-900/30">
                    <Upload className="w-6 h-6 text-slate-500 mb-1" />
                    <span className="text-xs text-slate-400">Cliquez pour uploader (PNG, SVG)</span>
                    <input
                      type="file"
                      accept=".png,.svg,.jpg,.jpeg"
                      className="hidden"
                      data-testid="upload-signature-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setUploadDebug("[signature] aucun fichier (e.target.files vide)");
                          return;
                        }
                        setUploadDebug(`[signature] 1/3 reçu : ${file.name} • ${(file.size/1024).toFixed(0)} KB • ${file.type || "?"}`);
                        try {
                          const formData = new FormData();
                          formData.append("file", file);
                          formData.append("type", "signature");
                          setUploadDebug(prev => prev + "\n2/3 envoi XHR...");
                          const res = await xhrUpload(
                            `${API}/api/platform/tenants/${tenantSlug}/upload-signature`,
                            formData,
                            { ...getAuthHeaders(), ...enteteTenant(tenantSlug) }
                          );
                          if (res.ok) {
                            updateField("signature_url", res.data?.url);
                            setUploadDebug(prev => prev + `\n3/3 OK ${res.status} • url=${res.data?.url}`);
                            toast.success("Signature uploadée");
                          } else {
                            setUploadDebug(prev => prev + `\n3/3 KO HTTP ${res.status} • ${typeof res.data === "string" ? res.data : JSON.stringify(res.data)}`);
                            toast.error(`Échec upload signature (HTTP ${res.status})`);
                          }
                        } catch (err) {
                          setUploadDebug(prev => prev + `\n3/3 ERREUR : ${err?.message || String(err)}`);
                          toast.error(`Erreur upload : ${err?.message || err}`);
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Tampon */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <Image className="w-4 h-4 text-rose-400" />
                  Tampon / Cachet
                </h4>
                {identite.tampon_url ? (
                  <div className="relative group">
                    <div className="rounded-lg border border-slate-700 p-3 bg-slate-900/50 flex items-center gap-3">
                      <img 
                        src={`${API}${identite.tampon_url}`} 
                        alt="Tampon" 
                        className="h-16 object-contain bg-white/5 rounded p-1" 
                        data-testid="tampon-preview"
                      />
                      <div className="flex-1">
                        <p className="text-xs text-emerald-400 font-medium">Tampon uploadé</p>
                        <p className="text-xs text-slate-500 truncate">{identite.tampon_url.split('/').pop()}</p>
                      </div>
                      <Button 
                        variant="ghost" size="sm" 
                        onClick={async () => {
                          if (!window.confirm("Supprimer le tampon ?")) return;
                          try {
                            const res = await fetch(`${API}/api/platform/tenants/${tenantSlug}/delete-signature?type=tampon`, {
                              method: "DELETE",
                              headers: { ...getAuthHeaders(), ...enteteTenant(tenantSlug) }
                            });
                            if (res.ok) {
                              updateField("tampon_url", "");
                              toast.success("Tampon supprimé");
                            } else {
                              const txt = await res.text().catch(() => "");
                              console.error("[delete tampon] HTTP", res.status, txt);
                              toast.error(`Échec suppression tampon (HTTP ${res.status})`);
                            }
                          } catch (err) {
                            console.error("Delete tampon error:", err);
                            toast.error(`Erreur suppression : ${err?.message || err}`);
                          }
                        }}
                        className="text-red-400 hover:text-red-300"
                        data-testid="delete-tampon-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-28 rounded-lg border-2 border-dashed border-slate-700 hover:border-rose-500/50 cursor-pointer transition-colors bg-slate-900/30">
                    <Upload className="w-6 h-6 text-slate-500 mb-1" />
                    <span className="text-xs text-slate-400">Cliquez pour uploader (PNG, SVG)</span>
                    <input
                      type="file"
                      accept=".png,.svg,.jpg,.jpeg"
                      className="hidden"
                      data-testid="upload-tampon-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setUploadDebug("[tampon] aucun fichier (e.target.files vide)");
                          return;
                        }
                        setUploadDebug(`[tampon] 1/3 reçu : ${file.name} • ${(file.size/1024).toFixed(0)} KB • ${file.type || "?"}`);
                        try {
                          const formData = new FormData();
                          formData.append("file", file);
                          formData.append("type", "tampon");
                          setUploadDebug(prev => prev + "\n2/3 envoi XHR...");
                          const res = await xhrUpload(
                            `${API}/api/platform/tenants/${tenantSlug}/upload-signature`,
                            formData,
                            { ...getAuthHeaders(), ...enteteTenant(tenantSlug) }
                          );
                          if (res.ok) {
                            updateField("tampon_url", res.data?.url);
                            setUploadDebug(prev => prev + `\n3/3 OK ${res.status} • url=${res.data?.url}`);
                            toast.success("Tampon uploadé");
                          } else {
                            setUploadDebug(prev => prev + `\n3/3 KO HTTP ${res.status} • ${typeof res.data === "string" ? res.data : JSON.stringify(res.data)}`);
                            toast.error(`Échec upload tampon (HTTP ${res.status})`);
                          }
                        } catch (err) {
                          setUploadDebug(prev => prev + `\n3/3 ERREUR : ${err?.message || String(err)}`);
                          toast.error(`Erreur upload : ${err?.message || err}`);
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        </Section>

        {/* Assurances */}
        <Section 
          title="Assurances" 
          icon={Shield} 
          iconColor="bg-blue-500/20 text-blue-400"
          isComplete={isAssuranceComplete}
        >
          <div className="space-y-6">
            {/* Décennale */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-400" />
                Assurance Décennale
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Compagnie *</label>
                  <Input
                    value={identite.assurance_decennale?.compagnie || ""}
                    onChange={(e) => updateNestedField("assurance_decennale", "compagnie", e.target.value)}
                    placeholder="Ex: AXA, Allianz..."
                    className="bg-slate-900/50 border-slate-600"
                    data-testid="assurance-decennale-compagnie"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">N° Police</label>
                  <Input
                    value={identite.assurance_decennale?.numero_police || ""}
                    onChange={(e) => updateNestedField("assurance_decennale", "numero_police", e.target.value)}
                    placeholder="XXXXXXXX"
                    className="bg-slate-900/50 border-slate-600"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Validité jusqu'au *</label>
                  <Input
                    type="date"
                    value={identite.assurance_decennale?.date_validite || ""}
                    onChange={(e) => updateNestedField("assurance_decennale", "date_validite", e.target.value)}
                    className="bg-slate-900/50 border-slate-600"
                  />
                </div>
              </div>
            </div>
            
            {/* RC Pro */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-purple-400" />
                Assurance Responsabilité Civile Professionnelle
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Compagnie</label>
                  <Input
                    value={identite.assurance_rc_pro?.compagnie || ""}
                    onChange={(e) => updateNestedField("assurance_rc_pro", "compagnie", e.target.value)}
                    placeholder="Ex: AXA, Allianz..."
                    className="bg-slate-900/50 border-slate-600"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">N° Police</label>
                  <Input
                    value={identite.assurance_rc_pro?.numero_police || ""}
                    onChange={(e) => updateNestedField("assurance_rc_pro", "numero_police", e.target.value)}
                    placeholder="XXXXXXXX"
                    className="bg-slate-900/50 border-slate-600"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Validité jusqu'au</label>
                  <Input
                    type="date"
                    value={identite.assurance_rc_pro?.date_validite || ""}
                    onChange={(e) => updateNestedField("assurance_rc_pro", "date_validite", e.target.value)}
                    className="bg-slate-900/50 border-slate-600"
                  />
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Coordonnées bancaires */}
        <Section 
          title="Coordonnées bancaires" 
          icon={CreditCard} 
          iconColor="bg-amber-500/20 text-amber-400"
          isComplete={identite.iban}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">IBAN</label>
              <Input
                value={identite.iban}
                onChange={(e) => updateField("iban", e.target.value)}
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                className="bg-slate-900/50 border-slate-600 font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">BIC</label>
              <Input
                value={identite.bic}
                onChange={(e) => updateField("bic", e.target.value)}
                placeholder="BNPAFRPP"
                className="bg-slate-900/50 border-slate-600 font-mono"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Banque</label>
              <Input
                value={identite.banque}
                onChange={(e) => updateField("banque", e.target.value)}
                placeholder="BNP Paribas"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
          </div>
        </Section>

        {/* Paramètres par défaut */}
        <Section 
          title="Paramètres par défaut" 
          icon={Settings} 
          iconColor="bg-zinc-800/300/20 text-slate-400"
          isComplete={true}
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Taux TVA par défaut (%)</label>
              <Input
                type="number"
                step="0.1"
                value={identite.taux_tva_defaut}
                onChange={(e) => updateField("taux_tva_defaut", parseFloat(e.target.value))}
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Délai de paiement (jours)</label>
              <Input
                type="number"
                value={identite.delai_paiement_defaut}
                onChange={(e) => updateField("delai_paiement_defaut", parseInt(e.target.value))}
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            {/* 🔴 ETI-113-B — adresses Pennylane : intégration ACTIVE seulement */}
            {hasIntegrationPennylane() && (<>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Email Pennylane (achats)</label>
              <Input
                value={identite.email_pennylane_achats}
                onChange={(e) => updateField("email_pennylane_achats", e.target.value)}
                placeholder="achats@xxx.pennylane.tech"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Email Pennylane (ventes)</label>
              <Input
                value={identite.email_pennylane_ventes}
                onChange={(e) => updateField("email_pennylane_ventes", e.target.value)}
                placeholder="ventes@xxx.pennylane.tech"
                className="bg-slate-900/50 border-slate-600"
              />
            </div>
            </>)}
          </div>
        </Section>

        {/* Mentions légales */}
        <Section 
          title="Mentions légales et CGV" 
          icon={Scale} 
          iconColor="bg-indigo-500/20 text-indigo-400"
          isComplete={identite.cgv}
        >
          <div className="space-y-6">
            {/* N°1003-B2 — inclusion des CGV dans le PDF devis */}
            <div className="flex items-center justify-between rounded-lg px-3 py-2"
                 style={{ border: "0.5px solid var(--oh-border)" }} data-testid="cgv-inclure-devis-ligne">
              <div>
                <label htmlFor="cgv-inclure-devis" className="text-sm" style={{ color: "var(--oh-text)" }}>
                  Inclure les CGV dans les devis
                </label>
                {cgvErreur && (
                  <p className="text-xs mt-1" role="alert" style={{ color: "var(--oh-error-text)" }}>{cgvErreur}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ color: "var(--oh-text-secondary)" }} data-testid="cgv-inclure-devis-etat">
                  {cgvIncluses === null ? "…" : cgvIncluses ? "Oui" : "Non"}
                </span>
                <Switch id="cgv-inclure-devis" data-testid="cgv-inclure-devis"
                        aria-label="Inclure les CGV dans les devis"
                        checked={!!cgvIncluses} disabled={cgvIncluses === null}
                        onCheckedChange={changerCgvIncluses} />
              </div>
            </div>

            {/* CGV avec éditeur intelligent */}
            <SmartLegalEditor
              label="Conditions Générales de Vente"
              description="Collez votre texte : les titres, articles et listes seront structurés automatiquement. Le HTML est généré en arrière-plan."
              value={identite.cgv}
              onChange={(html) => updateField("cgv", html)}
              placeholder="Collez vos CGV ici...

ARTICLE 1 - OBJET
Les présentes conditions générales de vente régissent les relations contractuelles...

ARTICLE 2 - PRIX
Les prix sont exprimés en euros TTC.

ARTICLE 3 - MODALITES DE PAIEMENT
- Acompte à la commande : 30%
- Solde à la livraison : 70%"
              minHeight="250px"
            />
            
            {/* Mentions légales avec éditeur simple */}
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Mentions légales</label>
              <Textarea
                value={identite.mentions_legales}
                onChange={(e) => updateField("mentions_legales", e.target.value)}
                placeholder="Mentions légales de votre entreprise..."
                className="bg-slate-900/50 border-slate-600 min-h-[100px]"
              />
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
